import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faUserDoctor,
  faClockRotateLeft,
  faEnvelope,
  faPhone,
  faUserPlus,
  faNotesMedical,
  faCalendarDay,
  faClock,
  faCircleCheck,
  faSpinner,
  faExclamationTriangle,
  faMapMarkerAlt,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import { useTeamMembers } from "../hooks/useTeamMembers";
import { useLocations } from "../hooks/useLocations";
import { bookingService } from "../lib/supabase";

const Bookings = () => {
  // Get today's date in the format yyyy-mm-dd
  const today = new Date().toISOString().split("T")[0];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [isDoctorDropdownOpen, setIsDoctorDropdownOpen] = useState(false);

  // Load locations for selection
  const { 
    locations
  } = useLocations(true);

  // Load team members available for booking, filtered by selected location (for form)
  const { 
    teamMembers,
    formatMemberSchedule 
  } = useTeamMembers(true, selectedLocationId);

  // Load ALL team members for display in the "Program Medici" section
  const { 
    teamMembers: allTeamMembers, 
    loading: allMembersLoading, 
    error: allMembersError,
    formatMemberSchedule: formatAllMemberSchedule 
  } = useTeamMembers(true, null);

  // Set default selected location when data loads
  useEffect(() => {
    if (locations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  // Reset selected doctor when location changes
  useEffect(() => {
    setSelectedDoctorId(null);
  }, [selectedLocationId]);

  // Set default selected doctor when team members load for a location
  useEffect(() => {
    if (teamMembers.length > 0 && !selectedDoctorId) {
      setSelectedDoctorId(teamMembers[0].id);
    }
  }, [teamMembers, selectedDoctorId]);

  // Helper to get selected location data
  const getSelectedLocation = () => {
    return locations.find(location => location.id === selectedLocationId);
  };

  // Helper to get selected doctor data
  const getSelectedDoctor = () => {
    return teamMembers.find(member => member.id === selectedDoctorId);
  };

  // Create refs for the date and time inputs and dropdowns
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const doctorDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setIsLocationDropdownOpen(false);
      }
      if (doctorDropdownRef.current && !doctorDropdownRef.current.contains(event.target)) {
        setIsDoctorDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [locationDropdownRef, doctorDropdownRef]);

  const handleDateLabelClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  const handleTimeLabelClick = () => {
    if (timeInputRef.current) {
      timeInputRef.current.showPicker();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Rate limiting: Prevent spam submissions (30 seconds cooldown)
    const now = Date.now();
    if (lastSubmissionTime && now - lastSubmissionTime < 30000) {
      const remainingTime = Math.ceil((30000 - (now - lastSubmissionTime)) / 1000);
      alert(`Vă rugăm să așteptați ${remainingTime} secunde înainte de a trimite o altă programare.`);
      return;
    }
    
    // Validate selections
    if (!selectedLocationId) {
      alert("Te rog să selectezi o clinică.");
      return;
    }
    
    if (!selectedDoctorId) {
      alert("Te rog să selectezi un doctor.");
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Get form data
      const form = e.target;
      const formData = new FormData(form);
      
      // Honeypot validation - if filled, it's likely a bot
      if (formData.get('website')) {
        console.log('🤖 Bot detected via honeypot field');
        return; // Silently reject without showing error
      }
      
      // Prepare booking data
      const bookingData = {
        firstName: formData.get('Prenume:'),
        lastName: formData.get('Nume:'),
        email: formData.get('Adresa de e-mail:'),
        phone: formData.get('Numar de telefon:'),
        locationId: selectedLocationId,
        doctorId: selectedDoctorId,
        preferredDate: formData.get('Data:'),
        preferredTime: formData.get('Ora:'),
        symptoms: formData.get('Descriere problema:')
      };

      // Create the booking in Supabase
      const newBooking = await bookingService.createBooking(bookingData);
      
      // Set submission timestamp for rate limiting
      setLastSubmissionTime(Date.now());
      
      // Try to send confirmation emails (don't fail if this fails)
      try {
        await bookingService.sendBookingEmail(newBooking);
        console.log('✅ Emails sent successfully');
      } catch (emailError) {
        console.error('⚠️ Email sending failed, but booking was created:', emailError);
        // Don't throw - we want to show success anyway since booking was created
      }
      
      // Success - reset form and show success message
      setIsSubmitting(false);
      setSubmitted(true);
      form.reset();
      
      // Reset state
      setSelectedLocationId(null);
      setSelectedDoctorId(null);

      // Reset form after showing success message
      setTimeout(() => {
        setSubmitted(false);
      }, 6000);
      
    } catch (error) {
      console.error('Booking submission error:', error);
      setIsSubmitting(false);
      
      // Show user-friendly error message
      if (error.message.includes('email')) {
        alert("Eroare la trimiterea emailului de confirmare. Programarea a fost salvată, dar vă rugăm să ne contactați telefonic pentru confirmare.");
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        alert("Eroare de conexiune. Vă rugăm să verificați internetul și să încercați din nou.");
      } else {
        alert("A apărut o eroare neașteptată. Vă rugăm să încercați din nou sau să ne contactați telefonic la 0725 596 895.");
      }
    }
  };

  return (
    <>
      <style>
        {`
          .doctor-schedule-all::-webkit-scrollbar {
            width: 8px;
          }
          .doctor-schedule-all::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .doctor-schedule-all::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 4px;
            transition: background 0.3s ease;
          }
          .doctor-schedule-all::-webkit-scrollbar-thumb:hover {
            background: #a1a1a1;
          }
        `}
      </style>
      <div className="bookings-page">
      <section className="bookings-hero">
        <div className="bookings-overlay"></div>
        <div className="container">
          <div className="bookings-hero-content">
            <h1>Programări Online</h1>
            <p>
              Rezervați o consultație medicală cu specialiștii noștri în doar
              câteva minute
            </p>
          </div>
        </div>
      </section>

      <section className="bookings-intro section">
        <div className="container">
          <div className="row">
            <div className="col-lg-7">
              <div className="bookings-intro-content">
                <h2>Programarea dumneavoastră este prioritatea noastră</h2>
                <p>
                  La Ali Cranta Medical Center, vă oferim posibilitatea de a
                  programa rapid o consultație, fără a fi nevoie să așteptați la
                  telefon. Completați formularul online și veți primi
                  confirmarea programării în cel mai scurt timp.
                </p>
                <p>
                  Echipa noastră medicală vă stă la dispoziție pentru
                  consultații de specialitate în pneumologie, geriatrie și alte
                  servicii specializate.
                </p>
                
                {/* Enhanced benefits section */}
                <div className="booking-benefits">
                  <div className="benefit-item">
                    <FontAwesomeIcon icon={faClockRotateLeft} className="benefit-icon" />
                    <span>Programare în 24h</span>
                  </div>
                  <div className="benefit-item">
                    <FontAwesomeIcon icon={faUserDoctor} className="benefit-icon" />
                    <span>Specialiști cu experiență</span>
                  </div>
                  <div className="benefit-item">
                    <FontAwesomeIcon icon={faNotesMedical} className="benefit-icon" />
                    <span>Consultații complete</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="schedule-container-enhanced" style={{ 
                border: '1px solid #e0e0e0', 
                borderRadius: '12px', 
                backgroundColor: '#f8f9fa',
                padding: '20px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden' // Prevent content from going outside
              }}>
                <div className="schedule-header">
                  <FontAwesomeIcon icon={faUserDoctor} className="schedule-icon" />
                  <h3>Echipa Noastră Medicală</h3>
                  <div className="schedule-subtitle">
                    Toți medicii disponibili pentru programări cu locațiile și programele lor
                  </div>
                </div>

                {allMembersLoading ? (
                  <div className="schedule-loading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Se încarcă echipa medicală...</span>
                  </div>
                ) : allMembersError ? (
                  <div className="schedule-error">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>Eroare la încărcarea echipei medicale</span>
                  </div>
                ) : allTeamMembers.length === 0 ? (
                  <div className="schedule-empty">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="empty-icon" />
                    <p>Nu sunt medici disponibili pentru programări momentan.</p>
                  </div>
                ) : (
                  <div className="doctor-schedule-all" style={{ 
                    maxHeight: '580px', 
                    overflowY: 'auto', 
                    paddingRight: '10px',
                    marginTop: '20px',
                    paddingBottom: '10px', // Add some bottom padding
                    // Custom scrollbar styling
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#c1c1c1 #f1f1f1'
                  }}>
                    {allTeamMembers.map((doctor) => (
                      <div key={doctor.id} className="doctor-card-enhanced" style={{
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '10px',
                        padding: '20px',
                        marginBottom: '15px',
                        marginTop: '5px', // Add top margin to prevent overflow
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        position: 'relative', // Ensure proper positioning
                        zIndex: 1 // Ensure cards stay in layer
                      }} onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.border = '0.5px solid #2196f3'; // Thicker border on hover
                        e.currentTarget.style.zIndex = '2';
                      }} onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.border = '1px solid #dee2e6'; // Reset to original border
                        e.currentTarget.style.zIndex = '1';
                      }}>
                        <div className="doctor-avatar" style={{ 
                          width: '60px', 
                          height: '60px', 
                          marginBottom: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#e3f2fd',
                          borderRadius: '50%',
                          border: '2px solid #2196f3'
                        }}>
                          {doctor.image_url ? (
                            <img 
                              src={doctor.image_url} 
                              alt={doctor.name}
                              className="doctor-image"
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                borderRadius: '50%', 
                                objectFit: 'cover' 
                              }}
                            />
                          ) : (
                            <FontAwesomeIcon icon={faUserDoctor} style={{ color: '#2196f3', fontSize: '24px' }} />
                          )}
                        </div>
                        <div className="doctor-info-enhanced">
                          <h4 className="doctor-name" style={{
                            color: '#1976d2',
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '5px'
                          }}>{doctor.name}</h4>
                          <p className="doctor-specialty" style={{
                            color: '#666',
                            fontSize: '14px',
                            fontWeight: '500',
                            marginBottom: '10px'
                          }}>{doctor.title}</p>
                          
                          {doctor.specialization && (
                            <div className="doctor-specialization-badge" style={{
                              backgroundColor: '#e8f5e8',
                              color: '#2e7d32',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '500',
                              marginBottom: '15px',
                              display: 'inline-block',
                              border: '1px solid #4caf50'
                            }}>
                              <FontAwesomeIcon icon={faNotesMedical} className="me-1" />
                              <span>{doctor.specialization}</span>
                            </div>
                          )}

                          {/* Doctor's Locations */}
                          {doctor.team_locations && doctor.team_locations.length > 0 && (
                            <div className="doctor-locations" style={{ marginBottom: '15px' }}>
                              <h6 className="locations-title" style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#555',
                                marginBottom: '8px'
                              }}>
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ color: '#ff5722' }} />
                                Locații de lucru:
                              </h6>
                              <div className="locations-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {doctor.team_locations.map((location, index) => (
                                  <div key={index} className="location-badge" style={{
                                    backgroundColor: '#fff3e0',
                                    color: '#e65100',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    border: '1px solid #ffcc02',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px'
                                  }}>
                                    <div style={{ fontWeight: '600' }}>
                                      <FontAwesomeIcon icon={faBuilding} className="me-1" />
                                      {location.title}
                                    </div>
                                    {location.phone && (
                                      <div className="location-contact" style={{ fontSize: '11px', color: '#bf360c' }}>
                                        <FontAwesomeIcon icon={faPhone} className="me-1" />
                                        {location.phone}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Doctor's Schedule */}
                          <div className="schedule-display-enhanced" style={{ marginBottom: '15px' }}>
                            <h6 className="schedule-title" style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#555',
                              marginBottom: '8px'
                            }}>
                              <FontAwesomeIcon icon={faClock} className="me-1" style={{ color: '#2196f3' }} />
                              Program:
                            </h6>
                            <div className="schedule-text" style={{
                              backgroundColor: '#e3f2fd',
                              color: '#1565c0',
                              padding: '10px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              lineHeight: '1.4',
                              border: '1px solid #bbdefb'
                            }}>
                              {formatAllMemberSchedule(doctor)}
                            </div>
                          </div>

                          {doctor.booking_notes && (
                            <div className="booking-notes-enhanced" style={{
                              backgroundColor: '#fff3e0',
                              color: '#e65100',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              border: '1px solid #ffcc02',
                              marginTop: '10px'
                            }}>
                              <FontAwesomeIcon icon={faExclamationTriangle} className="note-icon me-1" style={{ color: '#ff9800' }} />
                              <small className="note-text">{doctor.booking_notes}</small>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bookings-form-section section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-12">
              <div className="form-container">

                {submitted ? (
                  <div className="success-message">
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="success-icon"
                    />
                    <h3>Programare trimisă cu succes!</h3>
                    <p>
                      Vă mulțumim pentru solicitarea de programare. Veți fi
                      contactat în cel mai scurt timp pentru confirmare.
                    </p>
                  </div>
                ) : (
                  <form
                    className="booking-form"
                    onSubmit={handleSubmit}
                  >
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="inputFirstName">
                            <FontAwesomeIcon
                              icon={faUserPlus}
                              className="field-icon"
                            />{" "}
                            Nume:
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="inputFirstName"
                            name="Prenume:"
                            placeholder="Popa"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="inputLastName">
                            <FontAwesomeIcon
                              icon={faUserPlus}
                              className="field-icon"
                            />{" "}
                            Prenume:
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="inputLastName"
                            name="Nume:"
                            placeholder="Andrei"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="inputEmail">
                            <FontAwesomeIcon
                              icon={faEnvelope}
                              className="field-icon"
                            />{" "}
                            Adresa de email:
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="inputEmail"
                            name="Adresa de e-mail:"
                            placeholder="abc@xyz.com"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="inputPhone">
                            <FontAwesomeIcon
                              icon={faPhone}
                              className="field-icon"
                            />{" "}
                            Număr de telefon:
                          </label>
                          <input
                            type="tel"
                            className="form-control"
                            id="inputPhone"
                            name="Numar de telefon:"
                            placeholder="+40712545676"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-group">
                          <label htmlFor="inputLocation">
                            <FontAwesomeIcon
                              icon={faBuilding}
                              className="field-icon"
                            />{" "}
                            Selectează clinica:
                          </label>
                          <div
                            className="custom-dropdown-container"
                            ref={locationDropdownRef}
                          >
                            <div
                              className="custom-dropdown-selected"
                              onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                            >
                              <span>{getSelectedLocation()?.title || "Selectează o clinică"}</span>
                              <FontAwesomeIcon
                                icon={faBuilding}
                                className={`custom-dropdown-icon ${
                                  isLocationDropdownOpen ? "active" : ""
                                }`}
                              />
                            </div>
                            {isLocationDropdownOpen && (
                              <div className="custom-dropdown-options">
                                {locations.map((location) => (
                                  <div
                                    key={location.id}
                                    className={`custom-dropdown-option ${
                                      selectedLocationId === location.id ? "selected" : ""
                                    }`}
                                    onClick={() => {
                                      setSelectedLocationId(location.id);
                                      setIsLocationDropdownOpen(false);
                                    }}
                                  >
                                    <div className="location-option">
                                      <strong>{location.title}</strong>
                                      {location.address && (
                                        <div className="location-address">
                                          <small>
                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                            {location.address}
                                          </small>
                                        </div>
                                      )}
                                      {location.schedule && (
                                        <div className="location-schedule">
                                          <small>
                                            <FontAwesomeIcon icon={faClock} className="me-1" />
                                            {location.schedule}
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <input
                              type="hidden"
                              name="Clinica:"
                              value={getSelectedLocation()?.title || ""}
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-group">
                          <label htmlFor="inputDoctor">
                            <FontAwesomeIcon
                              icon={faUserDoctor}
                              className="field-icon"
                            />{" "}
                            Programare pentru:
                          </label>
                          <div
                            className="custom-dropdown-container"
                            ref={doctorDropdownRef}
                          >
                            <div
                              className="custom-dropdown-selected"
                              onClick={() => setIsDoctorDropdownOpen(!isDoctorDropdownOpen)}
                            >
                              <span>
                                {teamMembers.length === 0 && selectedLocationId
                                  ? "Nu sunt doctori disponibili la această locație"
                                  : getSelectedDoctor()?.name || "Selectează un doctor"
                                }
                              </span>
                              <FontAwesomeIcon
                                icon={faUserDoctor}
                                className={`custom-dropdown-icon ${
                                  isDoctorDropdownOpen ? "active" : ""
                                }`}
                              />
                            </div>
                            {isDoctorDropdownOpen && teamMembers.length > 0 && (
                              <div className="custom-dropdown-options">
                                {teamMembers.map((doctor) => (
                                  <div
                                    key={doctor.id}
                                    className={`custom-dropdown-option ${
                                      selectedDoctorId === doctor.id ? "selected" : ""
                                    }`}
                                    onClick={() => {
                                      setSelectedDoctorId(doctor.id);
                                      setIsDoctorDropdownOpen(false);
                                    }}
                                  >
                                    <div className="doctor-option">
                                      <strong>{doctor.name}</strong>
                                      {doctor.title && (
                                        <div className="doctor-title">
                                          <small>{doctor.title}</small>
                                        </div>
                                      )}
                                      {doctor.specialization && (
                                        <div className="doctor-specialization">
                                          <small className="text-info">
                                            <FontAwesomeIcon icon={faNotesMedical} className="me-1" />
                                            {doctor.specialization}
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <input
                              type="hidden"
                              name="Programare pentru:"
                              value={getSelectedDoctor()?.name || ""}
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-group">
                          <label htmlFor="inputDescription">
                            <FontAwesomeIcon
                              icon={faNotesMedical}
                              className="field-icon"
                            />{" "}
                            Descriere simptome:
                          </label>
                          <textarea
                            id="inputDescription"
                            className="form-control"
                            name="Descriere problema:"
                            placeholder="Doresc o programare pentru un examen pneumologic."
                            rows="4"
                            required
                          ></textarea>
                        </div>
                      </div>
                      {/* Honeypot field - hidden from users but visible to bots */}
                      <div style={{ display: 'none' }}>
                        <input
                          type="text"
                          name="website"
                          tabIndex="-1"
                          autoComplete="off"
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="form-group date-picker-wrapper">
                          <label
                            htmlFor="inputDate"
                            className="clickable-label"
                            onClick={handleDateLabelClick}
                          >
                            <FontAwesomeIcon
                              icon={faCalendarDay}
                              className="field-icon"
                            />{" "}
                            Data programării:
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="inputDate"
                            name="Data:"
                            min={today}
                            required
                            ref={dateInputRef}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group time-picker-wrapper">
                          <label
                            htmlFor="inputTime"
                            className="clickable-label"
                            onClick={handleTimeLabelClick}
                          >
                            <FontAwesomeIcon
                              icon={faClock}
                              className="field-icon"
                            />{" "}
                            Interval orar:
                          </label>
                          <input
                            type="time"
                            className="form-control"
                            id="inputTime"
                            name="Ora:"
                            onChange={(e) => {
                              const selectedTime = e.target.value;
                              if (
                                selectedTime < "08:00" ||
                                selectedTime > "20:00"
                              ) {
                                e.target.value = "08:00";
                              }
                            }}
                            required
                            ref={timeInputRef}
                          />
                          {getSelectedDoctor() && (
                            <small className="form-text">
                              <strong>Program {getSelectedDoctor().name}:</strong>{" "}
                              {formatMemberSchedule(getSelectedDoctor())}
                              {getSelectedDoctor().booking_notes && (
                                <div className="mt-1 text-info">
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                  {getSelectedDoctor().booking_notes}
                                </div>
                              )}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="col-12 text-center mt-4">
                        <button
                          type="submit"
                          className="btn-booking-submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <FontAwesomeIcon
                                icon={faSpinner}
                                spin
                                className="me-2"
                              />
                              Se trimite...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon
                                icon={faCalendarCheck}
                                className="me-2"
                              />
                              Solicită o programare
                            </>
                          )}
                        </button>
                        <p className="help-text mt-3">
                          <FontAwesomeIcon
                            icon={faPhone}
                            className="me-1"
                          />
                          Ai nevoie de ajutor? Sună-ne la{" "}
                          <a href="tel:0725596895" className="help-link">
                            0725 596 895
                          </a>
                        </p>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bookings-cta section">
        <div className="container">
          <div className="cta-content text-center">
            <h2>Dorești o programare rapidă?</h2>
            <p>Ne poți contacta și telefonic pentru o programare imediată</p>
            <div className="cta-phone">
              <FontAwesomeIcon icon={faPhone} className="me-2" />
              <a href="tel:0725596895">0725 596 895</a>
              <span className="mx-2">|</span>
              <a href="tel:0724284595">0724 284 595</a>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Bookings;
